import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack, Typography, Paper, TextField, MenuItem, Checkbox, FormControlLabel,
  Button, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Box, InputAdornment, CircularProgress, TablePagination, Select, Switch,
  ToggleButton, ToggleButtonGroup, Tooltip, IconButton, Menu,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MoreVertIcon from "@mui/icons-material/MoreVertOutlined";
import { ChevronUp, ChevronDown } from "lucide-react";
import { apiList, apiPatch, apiPost, apiGet, ApiError } from "../api/client";
import { cacheGet, cacheSet, cacheKey, cacheInvalidate } from "../api/cache";
import { usePersistedState } from "../api/persistedState";
import ImageGalleryCell from "../components/ImageGalleryCell";
import { useToast } from "../ui/Toaster";

export const CONDITION_OPTIONS = ["", "New", "Open Box", "Used", "For Parts"];

const SHELVES = Array.from({ length: 5 }, (_, j) => j + 1);
const POS_LIMIT = { a: 8, b: 5, c: 5, d: 7, e: 7, f: 7, g: 7 };
const ROW_LETTERS = ["a", "b", "c", "d", "e", "f", "g"];
const ROW_LOCATIONS = ROW_LETTERS.flatMap((row) => {
  const max = POS_LIMIT[row] || 0;
  const positions = Array.from({ length: max }, (_, i) => i + 1);
  return positions.flatMap((pos) => SHELVES.map((shelf) => `${row}${pos}-${shelf}`));
});
// "Returned (Assurance)" stays out of this dropdown ON PURPOSE — the
// canonical way to mark an item as assurance-returned is the per-row
// action button (and the dedicated /api/items/<lot_id>/mark_returned_assurance
// endpoint) because it ALSO has to rewrite purchases.cost down to the
// assurance fee tier ($5 or $10). Picking it from the dropdown would
// only update items.location and leave the cost intact, producing a
// misleading PnL. We DO still include it in the rendered Select's
// value list (see below) so an already-marked row shows the right text.
const LOCATION_OPTIONS = ["", "Home", "Warehouse", "Sold", "Trash", "Missing", "Personal", "Abandoned", "Refunded", "Other", ...ROW_LOCATIONS];

const SORT_OPTIONS = [
  { key: "purchase_date", dir: "desc", label: "Newest" },
  { key: "purchase_date", dir: "asc",  label: "Oldest" },
  { key: "value",         dir: "desc", label: "Value (high→low)" },
  { key: "cleaned_title", dir: "asc",  label: "Title A→Z" },
];

// Per-lot sort fields -> consolidated equivalents. Lets the existing sort
// dropdown keep working without forking the menu when consolidate=true is
// active on the server.
const CONSOLIDATED_SORT_ALIAS = {
  value: "total_value",
  spend: "total_spend",
};

function useDebounced(value, ms = 300) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return d;
}

function formatMoney(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}
function formatDate(unix) {
  if (!unix) return "—";
  return new Date(Number(unix) * 1000).toLocaleDateString();
}

function StatusChip({ row }) {
  if (row.sold) {
    return <Chip size="small" label="Sold" color="success" variant="outlined" />;
  }
  // Paid-but-unshipped on eBay — operator hasn't run update_sales yet.
  // Loud warning chip so it's obvious this lot is *physically committed*
  // even though sold=false (no sale_lots row yet).
  if (Number(row.unfulfilled_qty) > 0) {
    return (
      <Chip
        size="small"
        label={row.unfulfilled_qty > 1 ? `Pending sale ×${row.unfulfilled_qty}` : "Pending sale"}
        color="warning"
        variant="filled"
        sx={{ fontWeight: 600 }}
      />
    );
  }
  if (row.listed) {
    return <Chip size="small" label="Listed" color="primary" variant="outlined" />;
  }
  return <Chip size="small" label="Idle" variant="outlined" />;
}

const COLUMNS = [
  { key: "_select",      header: "",          width: 42,  sortable: false },
  { key: "image_urls",   header: "Image",     width: 84,  sortable: false },
  { key: "lot_id",       header: "Lot",       width: 130, sortable: true },
  { key: "cleaned_title", header: "Title",    width: undefined, sortable: true },
  { key: "sold",         header: "Status",    width: 110, sortable: false },
  { key: "value",        header: "Value",     width: 100, sortable: true,  align: "right" },
  { key: "spend",        header: "Spend",     width: 100, sortable: true,  align: "right" },
  { key: "purchase_date",header: "Acquired",  width: 120, sortable: true },
  { key: "condition",    header: "Condition", width: 150, sortable: false },
  { key: "location",     header: "Location",  width: 150, sortable: false },
];

// Consolidated (group-by-title) columns. The detail page (route added in a
// later phase) handles per-lot edits, so no inline location/condition select
// here. Matches the spec on /api/inventory?consolidate=true field names.
const CONSOLIDATED_COLUMNS = [
  { key: "_select",       header: "",            width: 42,  sortable: false },
  { key: "image_urls",    header: "Image",       width: 84,  sortable: false },
  { key: "cleaned_title", header: "Title",       width: undefined, sortable: true },
  { key: "purchase_date", header: "Acquired",    width: 110, sortable: true },
  { key: "qty",           header: "Qty",         width: 110, sortable: true },
  { key: "avg_cost",      header: "Avg cost",    width: 100, sortable: true,  align: "right" },
  { key: "total_spend",   header: "Total spend", width: 110, sortable: true,  align: "right" },
  { key: "total_value",   header: "Value",       width: 100, sortable: true,  align: "right" },
  { key: "locations",     header: "Locations",   width: 180, sortable: false },
  { key: "conditions",    header: "Conditions",  width: 180, sortable: false },
  { key: "linked",        header: "Linked",      width: 110, sortable: false },
  { key: "status",        header: "Status",      width: 160, sortable: false },
];

export default function Inventory() {
  const { error: toastError, success: toastSuccess } = useToast();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePersistedState("bidrl_inventory_page_size", 50);
  const [search, setSearch] = useState("");
  const [showSold, setShowSold] = usePersistedState("bidrl_inventory_show_sold", false);
  const [notReceived, setNotReceived] = usePersistedState("bidrl_inventory_not_received", false);
  const [unprocessed, setUnprocessed] = usePersistedState("bidrl_inventory_unprocessed", false);
  const [retailers, setRetailers] = useState([]);          // [{name, count}]
  const [selectedRetailers, setSelectedRetailers] = usePersistedState(
    "bidrl_inventory_selected_retailers", []
  );

  // Bulk selection on the table. ``selectedKeys`` holds the row identifier
  // (consolidated `key` or per-lot `lot_id`); ``anchorIndex`` is where the
  // last single-click landed so shift-click can extend a range. Cleared on
  // page/filter changes since the row indices shift underneath.
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [anchorIndex, setAnchorIndex] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sortKey, setSortKey] = usePersistedState("bidrl_inventory_sort_key", SORT_OPTIONS[0].key);
  const [sortDir, setSortDir] = usePersistedState("bidrl_inventory_sort_dir", SORT_OPTIONS[0].dir);
  // "Group by title" — consolidates the per-lot rows into one row per
  // distinct cleaned_title slug. Default ON; persisted to localStorage so
  // the user's last choice sticks across sessions.
  const [consolidate, setConsolidate] = useState(() => {
    try {
      const v = localStorage.getItem("bidrl_inventory_consolidate");
      return v === null ? true : v === "true";
    } catch {
      return true;
    }
  });
  // Imbalance filter — array of {overlisted, underlisted, balanced, unlisted}.
  // Empty = show all. Only meaningful in consolidated mode (the per-lot view
  // doesn't aggregate listing qty), but the chips render either way and are
  // simply ignored when consolidate is off.
  const [imbalanceFilter, setImbalanceFilter] = usePersistedState(
    "bidrl_inventory_imbalance_filter", []
  );
  // Lot-prefix filter — array of prefixes like ["NN", "VB", "ORF"]. Empty
  // = show all. Backend applies as an OR — an item matches when its lot_id
  // starts with any of the selected prefixes. Persisted so the operator's
  // last slice survives reloads.
  const [lotPrefixFilter, setLotPrefixFilter] = usePersistedState(
    "bidrl_inventory_lot_prefix_filter", []
  );
  const lotPrefixKey = useMemo(
    () => lotPrefixFilter.slice().sort().join(","),
    [lotPrefixFilter]
  );
  // Listing status filter — "" = all, "listed" = items.listed=1, "unlisted"
  // = items.listed=0. Works in both per-lot and consolidated modes (unlike
  // the imbalance toggle's Unlisted bucket, which is consolidate-only).
  // Backend maps to ?listed_status=…
  const [listedStatus, setListedStatus] = usePersistedState(
    "bidrl_inventory_listed_status", ""
  );
  // Group keys with an in-flight reconcile_qty call — disables the chip and
  // shows a spinner while the eBay round-trip happens.
  const [adjustingKeys, setAdjustingKeys] = useState(() => new Set());

  const debouncedSearch = useDebounced(search, 300);

  const retailerKey = useMemo(() => selectedRetailers.slice().sort().join("|"), [selectedRetailers]);

  const load = useCallback(
    async (signal) => {
      const effectiveSortKey = consolidate
        ? (CONSOLIDATED_SORT_ALIAS[sortKey] || sortKey)
        : sortKey;
      const params = {
        page,
        page_size: pageSize,
        q: debouncedSearch,
        show_sold: showSold && !notReceived && !unprocessed ? "true" : "false",
        not_received: notReceived && !unprocessed ? "true" : "false",
        unprocessed: unprocessed ? "true" : "false",
        sort: effectiveSortKey,
        order: sortDir,
        consolidate: consolidate ? "true" : "",
        source: retailerKey,
      };
      if (consolidate && Array.isArray(imbalanceFilter) && imbalanceFilter.length > 0) {
        params.imbalance = imbalanceFilter.join(",");
      }
      if (lotPrefixKey) {
        params.lot_prefix = lotPrefixKey;
      }
      if (listedStatus === "listed" || listedStatus === "unlisted") {
        params.listed_status = listedStatus;
      }
      const ck = cacheKey("/api/inventory", params);
      const cached = cacheGet(ck);
      if (cached) {
        // Stale-while-revalidate: paint cached rows now, fetch fresh in the
        // background so the table updates silently if the server has moved on.
        setRows(cached.data.items || []);
        setTotal(cached.data.total || 0);
        setLoading(false);
      } else {
        setLoading(true);
      }
      try {
        const res = await apiList("/api/inventory", params, { signal });
        if (signal?.aborted) return;
        setRows(res.items || []);
        setTotal(res.total || 0);
        cacheSet(ck, res);
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (!cached) {
          toastError("Couldn't load inventory", e instanceof ApiError ? e.message : String(e));
          setRows([]);
          setTotal(0);
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [page, pageSize, debouncedSearch, showSold, notReceived, unprocessed, sortKey, sortDir, consolidate, retailerKey, imbalanceFilter, lotPrefixKey, listedStatus, toastError]
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  // Retailer chip choices — small payload, fetched once.
  useEffect(() => {
    let cancelled = false;
    apiGet("/api/inventory/retailers").then((data) => {
      if (!cancelled) setRetailers(Array.isArray(data) ? data : []);
    }).catch(() => { /* silent — non-critical */ });
    return () => { cancelled = true; };
  }, []);

  // Reset to page 1 when filters change (including the consolidate toggle).
  useEffect(() => { setPage(1); }, [debouncedSearch, showSold, notReceived, unprocessed, sortKey, sortDir, pageSize, consolidate, retailerKey, imbalanceFilter, lotPrefixKey, listedStatus]);

  // Click "Adjust" on the over/under chip → call the per-group reconcile.
  // Backend sets each keeper's qty to its partition's unsold count, then we
  // patch the row in place so the chip flips to balanced without a refetch.
  const adjustQty = useCallback(async (row) => {
    if (!row?.key) return;
    setAdjustingKeys((prev) => {
      const n = new Set(prev);
      n.add(row.key);
      return n;
    });
    try {
      const res = await apiPost(`/api/inventory/${encodeURIComponent(row.key)}/reconcile_qty`);
      if (res?.success) {
        const n = res.adjusted_count || (res.adjustments || []).length;
        if (n > 0) {
          toastSuccess("Qty adjusted", `${n} listing(s) updated for ${row.cleaned_title || row.key}`);
        } else {
          toastSuccess("Already in sync", `Nothing to fix for ${row.cleaned_title || row.key}`);
        }
        // Optimistic local update so the chip flips immediately. The next
        // /api/inventory call (cache busted by the backend) reconciles to
        // truth; until then, mark this row balanced.
        setRows((cur) =>
          cur.map((r) =>
            r.key === row.key
              ? { ...r, listed_qty: r.unsold_qty, listing_imbalance: "balanced" }
              : r
          )
        );
        cacheInvalidate("/api/inventory");
        cacheInvalidate("/api/listings");
      } else {
        toastError("Couldn't adjust", res?.error || "Reconcile failed");
      }
    } catch (e) {
      toastError("Couldn't adjust", e instanceof ApiError ? e.message : String(e));
    } finally {
      setAdjustingKeys((prev) => {
        const n = new Set(prev);
        n.delete(row.key);
        return n;
      });
    }
  }, [toastSuccess, toastError]);

  // Wipe selection on anything that changes the visible row order — index
  // anchors only make sense against the current rows array.
  useEffect(() => {
    setSelectedKeys(new Set());
    setAnchorIndex(null);
  }, [page, pageSize, debouncedSearch, showSold, notReceived, unprocessed, sortKey, sortDir, consolidate, retailerKey]);

  // Stable per-row key. Consolidated view groups by title slug; per-lot uses lot_id.
  const rowKey = useCallback((row) => (consolidate ? row.key : row.lot_id), [consolidate]);

  // toggleAt handles single-click (toggle this row, set anchor) and shift-
  // click (select range from the anchor to the clicked row, regardless of
  // each row's prior state).
  const toggleAt = useCallback((index, withShift) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const r = rows[index];
      if (!r) return prev;
      const k = rowKey(r);
      if (withShift && anchorIndex !== null && anchorIndex !== index) {
        const [from, to] = anchorIndex < index ? [anchorIndex, index] : [index, anchorIndex];
        for (let i = from; i <= to; i += 1) {
          const ri = rows[i];
          if (ri) next.add(rowKey(ri));
        }
      } else {
        if (next.has(k)) next.delete(k);
        else next.add(k);
        setAnchorIndex(index);
      }
      return next;
    });
  }, [rows, anchorIndex, rowKey]);

  const allVisibleSelected = rows.length > 0 && rows.every((r) => selectedKeys.has(rowKey(r)));
  const someVisibleSelected = !allVisibleSelected && rows.some((r) => selectedKeys.has(rowKey(r)));
  const toggleAllVisible = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const r of rows) next.delete(rowKey(r));
      } else {
        for (const r of rows) next.add(rowKey(r));
      }
      return next;
    });
    setAnchorIndex(null);
  };
  const clearSelection = () => {
    setSelectedKeys(new Set());
    setAnchorIndex(null);
  };

  // Resolve selected keys back to the underlying lot_ids that bulk actions
  // should operate on. For consolidated rows that's the group's lot_ids; for
  // per-lot rows it's the row's own lot_id. Filters out anything already sold.
  const selectedLotIds = useMemo(() => {
    if (selectedKeys.size === 0) return [];
    const out = [];
    for (const r of rows) {
      const k = rowKey(r);
      if (!selectedKeys.has(k)) continue;
      if (consolidate) {
        const lotIds = Array.isArray(r.lot_ids) ? r.lot_ids : [];
        for (const lid of lotIds) {
          if (lid) out.push(lid);
        }
      } else if (!r.sold && r.lot_id) {
        out.push(r.lot_id);
      }
    }
    return out;
  }, [rows, selectedKeys, rowKey, consolidate]);

  const bulkRelist = useCallback(async () => {
    if (selectedLotIds.length === 0) return;
    if (!window.confirm(`Queue ${selectedLotIds.length} lot${selectedLotIds.length === 1 ? "" : "s"} for relisting? Existing matching listings will have their qty bumped instead of being duplicated.`)) {
      return;
    }
    setBulkBusy(true);
    let queued = 0;
    let failed = 0;
    for (const lid of selectedLotIds) {
      try {
        await apiPost(`/api/list_item/${encodeURIComponent(lid)}`);
        queued += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkBusy(false);
    if (failed === 0) {
      toastSuccess("Queued", `${queued} lot${queued === 1 ? "" : "s"} sent to lister`);
    } else {
      toastError(`${failed} failed, ${queued} queued`, "Lister errors logged on the server.");
    }
    cacheInvalidate("/api/inventory");
    clearSelection();
  }, [selectedLotIds, toastSuccess, toastError]);

  // Persist the toggle so the user's last view is restored on next visit.
  useEffect(() => {
    try { localStorage.setItem("bidrl_inventory_consolidate", String(consolidate)); } catch { /* ignore */ }
  }, [consolidate]);

  // Inline location/condition update with optimistic UI + error rollback.
  const updateField = useCallback(
    async (lotId, field, value) => {
      const prev = rows;
      setRows((cur) => cur.map((r) => (r.lot_id === lotId ? { ...r, [field]: value } : r)));
      try {
        const path = field === "location" ? "/api/update_location" : "/api/update_condition";
        await apiPatch(path, { lot_id: lotId, [field]: value });
        // Cached inventory pages now show stale field — drop them so the next
        // visit (or background revalidate) repopulates with the new value.
        cacheInvalidate("/api/inventory");
        toastSuccess(`Updated ${field}`, `${lotId} → ${value || "(empty)"}`);
      } catch (e) {
        setRows(prev); // rollback
        toastError(`Failed to update ${field}`, e instanceof ApiError ? e.message : String(e));
      }
    },
    [rows, toastSuccess, toastError]
  );

  // Per-item action menu state. ``menuLot`` holds the lot_id of the
  // row whose kebab is open; ``anchorEl`` is the DOM node it pops from.
  const [itemMenuAnchor, setItemMenuAnchor] = useState(null);
  const [itemMenuLot, setItemMenuLot] = useState(null);
  const [itemMenuCost, setItemMenuCost] = useState(0);
  const closeItemMenu = () => { setItemMenuAnchor(null); setItemMenuLot(null); };

  // Shared mark-helper used by all three item actions. POSTs to one of
  // the dedicated endpoints, updates the row optimistically with the
  // new location, and rolls back on failure.
  const _runItemMark = useCallback(
    async ({ lotId, endpoint, body, location, confirmMsg, toast }) => {
      if (confirmMsg && !window.confirm(confirmMsg)) return;
      const prev = rows;
      setRows((cur) => cur.map((r) =>
        r.lot_id === lotId ? { ...r, location } : r
      ));
      try {
        const r = await apiPost(
          `/api/items/${encodeURIComponent(lotId)}${endpoint}`,
          body || undefined,
        );
        if (r?.success) {
          cacheInvalidate("/api/inventory");
          toastSuccess(toast.title, toast.msg);
        } else {
          setRows(prev);
          toastError("Mark failed", r?.error || "Unknown error");
        }
      } catch (e) {
        setRows(prev);
        toastError("Mark failed", e instanceof ApiError ? e.message : String(e));
      }
    },
    [rows, toastSuccess, toastError]
  );

  const markPersonal = useCallback((lotId) =>
    _runItemMark({
      lotId, endpoint: "/mark_personal", location: "Personal",
      confirmMsg: `Mark ${lotId} as Personal?\n\n`
        + "Item is hidden from sellable inventory; full cost shows as a "
        + "personal-withdrawal write-off. Auto-delists from eBay/FB if listed.",
      toast: { title: "Marked Personal", msg: lotId },
    }),
    [_runItemMark]);

  // Mark a single item as Returned via the auctionsoftware buyer's
  // assurance plugin. Tiered fee: $5 if original cost < $100, $10 above.
  const markReturnedAssurance = useCallback((lotId, originalCost) => {
    const fee = Number(originalCost || 0) >= 100 ? 10 : 5;
    return _runItemMark({
      lotId, endpoint: "/mark_returned_assurance",
      body: { assurance_fee: fee },
      location: "Returned (Assurance)",
      confirmMsg: `Mark ${lotId} as Returned via Buyer's Assurance?\n\n`
        + `Effective cost becomes $${fee.toFixed(2)} (assurance fee). `
        + `Item hidden from sellable inventory; auto-delists from eBay/FB.`,
      toast: { title: "Marked Returned (Assurance)", msg: `${lotId} → $${fee.toFixed(2)} loss` },
    });
  }, [_runItemMark]);

  // Single-item full refund: cost zeroed, location set to Refunded.
  // For when the auction site refunds you for ONE item (not the whole
  // invoice). Auto-delists if listed.
  const markRefunded = useCallback((lotId) =>
    _runItemMark({
      lotId, endpoint: "/mark_refunded", location: "Refunded",
      confirmMsg: `Mark ${lotId} as Refunded?\n\n`
        + "Cost zeroed (you got your money back). Item stays in the DB "
        + "for the audit trail with location='Refunded'. Auto-delists "
        + "from eBay/FB if listed.",
      toast: { title: "Marked Refunded", msg: lotId },
    }),
    [_runItemMark]);

  const onHeaderClick = useCallback((col) => {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col.key);
      setSortDir("asc");
    }
  }, [sortKey, setSortDir, setSortKey]);

  return (
    <Box>
      {/* Page header — matches OC's standard layout (h1 serif title + secondary subtitle + right-aligned actions) */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.85rem', md: '2.5rem' } }}>
            Inventory
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {total.toLocaleString()}{" "}
            {consolidate
              ? `product${total === 1 ? "" : "s"}`
              : `item${total === 1 ? "" : "s"}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="contained"
            onClick={() => load()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Filters card */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search title, lot id, brand, UPC, ASIN…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            value={`${sortKey}|${sortDir}`}
            onChange={(e) => {
              const [k, d] = e.target.value.split("|");
              setSortKey(k);
              setSortDir(d);
            }}
            sx={{ minWidth: 180 }}
          >
            {SORT_OPTIONS.map((o) => (
              <MenuItem key={o.key + o.dir} value={`${o.key}|${o.dir}`}>{o.label}</MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={showSold}
                disabled={notReceived || unprocessed}
                onChange={(e) => setShowSold(e.target.checked)}
                size="small"
              />
            }
            label="Show sold"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={notReceived}
                disabled={unprocessed}
                onChange={(e) => setNotReceived(e.target.checked)}
                size="small"
              />
            }
            label="Not received"
          />
          <Tooltip title="Items with $0 value — never priced/researched. The fresh-pallet queue.">
            <FormControlLabel
              control={
                <Checkbox
                  checked={unprocessed}
                  onChange={(e) => setUnprocessed(e.target.checked)}
                  size="small"
                />
              }
              label="Unprocessed"
            />
          </Tooltip>
          <FormControlLabel
            control={
              <Switch
                checked={consolidate}
                onChange={(e) => setConsolidate(e.target.checked)}
                size="small"
              />
            }
            label="Group by title"
          />
        </Stack>
        {/* Listing-status filter — always visible so the operator can slice
            per-lot mode by "haven't been listed on eBay yet" (the common
            heal-my-backlog case). Backend applies ``?listed_status=…``
            against items.listed for both per-lot and consolidated views. */}
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", mr: 0.5 }}>
            Listing status:
          </Typography>
          <ToggleButtonGroup
            value={listedStatus || ""}
            exclusive
            size="small"
            onChange={(_, v) => setListedStatus(v || "")}
            aria-label="Filter by listing status"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none', fontSize: 13, px: 1.5, py: 0.25,
              },
            }}
          >
            <ToggleButton value="">All</ToggleButton>
            <ToggleButton value="listed">Listed</ToggleButton>
            <ToggleButton value="unlisted" color="warning">Unlisted</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {/* Listed-vs-unsold imbalance filter — only meaningful in
            consolidated mode since the per-lot view doesn't aggregate
            listing qty. Hidden when consolidate is off so the strip
            doesn't show a control that does nothing. */}
        {consolidate && (
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", mr: 0.5 }}>
              Imbalance:
            </Typography>
            <ToggleButtonGroup
              value={Array.isArray(imbalanceFilter) ? imbalanceFilter : []}
              size="small"
              onChange={(_, v) => setImbalanceFilter(Array.isArray(v) ? v : [])}
              aria-label="Filter by qty imbalance"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none', fontSize: 13, px: 1.5, py: 0.25,
                },
              }}
            >
              <ToggleButton value="overlisted" color="warning">Over-listed</ToggleButton>
              <ToggleButton value="underlisted" color="info">Under-listed</ToggleButton>
              <ToggleButton value="balanced">Balanced</ToggleButton>
              <ToggleButton value="unlisted">Unlisted</ToggleButton>
            </ToggleButtonGroup>
            {imbalanceFilter.length > 0 && (
              <Chip
                size="small"
                label="Clear"
                variant="outlined"
                onClick={() => setImbalanceFilter([])}
                sx={{ ml: 0.5 }}
              />
            )}
          </Stack>
        )}
        {/* Lot-prefix filter — three retailer-style buckets. Multi-select:
            clicking multiple chips ORs them together on the backend so
            "NN + ORF" shows lots from either. Clear chip drops the filter
            entirely. Fixed set (not derived from data) because these are
            the operator's canonical sourcing buckets and adding a new one
            is intentional. */}
        <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", mr: 0.5 }}>
            Lot prefix:
          </Typography>
          {["NN", "VB", "ORF"].map((p) => {
            const active = lotPrefixFilter.includes(p);
            return (
              <Chip
                key={p}
                size="small"
                label={p}
                variant={active ? "filled" : "outlined"}
                color={active ? "primary" : "default"}
                onClick={() => {
                  setLotPrefixFilter((prev) =>
                    prev.includes(p)
                      ? prev.filter((x) => x !== p)
                      : [...prev, p]
                  );
                }}
                sx={{ fontFamily: "monospace", fontWeight: 700 }}
              />
            );
          })}
          {lotPrefixFilter.length > 0 && (
            <Chip
              size="small"
              label="Clear"
              variant="outlined"
              onClick={() => setLotPrefixFilter([])}
              sx={{ ml: 0.5 }}
            />
          )}
        </Stack>
        {retailers.length > 0 && (
          <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", mr: 0.5 }}>
              Retailer:
            </Typography>
            {retailers.map((r) => {
              const active = selectedRetailers.includes(r.name);
              return (
                <Chip
                  key={r.name}
                  size="small"
                  label={`${r.name} (${r.count})`}
                  variant={active ? "filled" : "outlined"}
                  color={active ? "primary" : "default"}
                  onClick={() => {
                    setSelectedRetailers((prev) =>
                      prev.includes(r.name)
                        ? prev.filter((x) => x !== r.name)
                        : [...prev, r.name]
                    );
                  }}
                />
              );
            })}
            {selectedRetailers.length > 0 && (
              <Chip
                size="small"
                label="Clear"
                variant="outlined"
                onClick={() => setSelectedRetailers([])}
                sx={{ ml: 0.5 }}
              />
            )}
          </Stack>
        )}
      </Paper>

      {/* Bulk action toolbar — only shown when something is selected. */}
      {selectedKeys.size > 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.25,
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            position: "sticky",
            top: 56,            // sit just under the TopBar
            zIndex: 5,
            bgcolor: "primary.50",
            borderColor: "primary.light",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {selectedKeys.size} selected
            {selectedLotIds.length > 0 && selectedLotIds.length !== selectedKeys.size && (
              <Box component="span" sx={{ ml: 0.5, color: "text.secondary", fontWeight: 400 }}>
                ({selectedLotIds.length} lot{selectedLotIds.length === 1 ? "" : "s"})
              </Box>
            )}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            disabled={bulkBusy || selectedLotIds.length === 0}
            onClick={bulkRelist}
            startIcon={bulkBusy ? <CircularProgress size={14} color="inherit" /> : <SyncIcon fontSize="small" />}
          >
            Relist {selectedLotIds.length}
          </Button>
          <Button size="small" variant="text" onClick={clearSelection} disabled={bulkBusy}>
            Clear
          </Button>
        </Paper>
      )}

      {/* Table */}
      <Paper variant="outlined" sx={{ overflow: 'hidden', position: 'relative' }}>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                {(consolidate ? CONSOLIDATED_COLUMNS : COLUMNS).map((col) => {
                  if (col.key === "_select") {
                    return (
                      <TableCell
                        key={col.key}
                        padding="checkbox"
                        sx={{ userSelect: 'none' }}
                      >
                        <Checkbox
                          size="small"
                          checked={allVisibleSelected}
                          indeterminate={someVisibleSelected}
                          onChange={toggleAllVisible}
                          onClick={(e) => e.stopPropagation()}
                          inputProps={{ "aria-label": "select all visible" }}
                        />
                      </TableCell>
                    );
                  }
                  const active = sortKey === col.key;
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align || "left"}
                      style={col.width ? { width: col.width } : undefined}
                      sx={{
                        cursor: col.sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                      onClick={() => onHeaderClick(col)}
                    >
                      <Stack direction={col.align === "right" ? "row-reverse" : "row"} alignItems="center" gap={0.5} sx={{ display: 'inline-flex' }}>
                        {col.header}
                        {col.sortable && active && (
                          sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                        )}
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={(consolidate ? CONSOLIDATED_COLUMNS : COLUMNS).length} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={(consolidate ? CONSOLIDATED_COLUMNS : COLUMNS).length} align="center" sx={{ py: 6 }}>
                    <Stack alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                      <Inventory2OutlinedIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>No inventory matches</Typography>
                      <Typography variant="caption">
                        {notReceived
                          ? "No WizBid items are missing a location."
                          : "Try adjusting your search or unchecking 'Show sold'."}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {consolidate && rows.map((row, rowIndex) => {
                const conds = Array.isArray(row.conditions) ? row.conditions : [];
                const condsShown = conds.slice(0, 3);
                const condsExtra = conds.length - condsShown.length;
                const locs = Array.isArray(row.locations) ? row.locations : [];
                const sold = Number(row.sold_qty) || 0;
                const active = Number(row.qty) || 0;
                const totalQty = Number(row.total_qty) || (active + sold);
                const selected = selectedKeys.has(row.key);
                return (
                  <TableRow
                    key={row.key}
                    hover
                    selected={selected}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admint/inventory/${encodeURIComponent(row.key)}`)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selected}
                        onClick={(e) => { e.stopPropagation(); toggleAt(rowIndex, e.shiftKey); }}
                        onChange={() => { /* handled in onClick to read shiftKey */ }}
                        inputProps={{ "aria-label": `select ${row.cleaned_title || row.key}` }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 84 }}>
                      <ImageGalleryCell imageUrlsStr={row.image_urls} thumbnailSize={48} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 0, maxWidth: 360 }}>
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.cleaned_title || row.title || "—"}
                        </Typography>
                        {row.brand && (
                          <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                            {row.brand}
                          </Typography>
                        )}
                        {/* Lot IDs inside the group — surfaced so the
                            operator can see which physical bins the group
                            spans without drilling into the detail page.
                            First 4 render inline; the rest collapse into
                            a "+N" chip whose tooltip lists them. Each chip
                            navigates to that lot's detail page. */}
                        {Array.isArray(row.lot_ids) && row.lot_ids.length > 0 && (
                          <Stack
                            direction="row"
                            gap={0.5}
                            flexWrap="wrap"
                            sx={{ mt: 0.5 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {row.lot_ids.slice(0, 4).map((lid) => (
                              <Chip
                                key={lid}
                                size="small"
                                label={lid}
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/admint/inventory/${encodeURIComponent(lid)}`);
                                }}
                                sx={{
                                  height: 20,
                                  fontFamily: "monospace",
                                  fontSize: 11,
                                  cursor: "pointer",
                                }}
                              />
                            ))}
                            {row.lot_ids.length > 4 && (
                              <Tooltip title={row.lot_ids.slice(4).join(", ")}>
                                <Chip
                                  size="small"
                                  label={`+${row.lot_ids.length - 4}`}
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: 11 }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {formatDate(row.purchase_date)}
                    </TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }} onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
                        <span>{sold > 0 ? `${totalQty} (${sold} sold)` : `${totalQty}`}</span>
                        {/* Listed-vs-unsold delta chip. Click sets each
                            keeper's qty to its partition's unsold count
                            (the "Adjust" action mirrored from EbayListings). */}
                        {/* Pending = lots in the group with no location yet.
                            Surfaced as a small chip so the operator can see
                            "+3 pending" alongside the unsold count and go
                            assign locations. These are intentionally NOT
                            counted in the over/under-listed math — they
                            aren't physically in hand yet. */}
                        {Number(row.pending_qty) > 0 && (
                          <Tooltip title={`${row.pending_qty} unsold item(s) have no location yet — not counted toward over/under-listed math until processed.`}>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`+${row.pending_qty} pending`}
                              sx={{ height: 22, color: "text.secondary", borderStyle: "dashed" }}
                            />
                          </Tooltip>
                        )}
                        {/* OVERSOLD: paid eBay orders exceed in-hand inventory.
                            Hard red flag — operator needs to cancel/refund or
                            source more units. */}
                        {Number(row.oversold_qty) > 0 && (
                          <Tooltip title={`OVERSOLD: ${row.oversold_qty} more unit(s) sold on eBay than we have in hand. Cancel or refund the extra order(s).`}>
                            <Chip
                              size="small"
                              color="error"
                              variant="filled"
                              label={`OVERSOLD ${row.oversold_qty}`}
                              sx={{ height: 22, fontWeight: 700 }}
                            />
                          </Tooltip>
                        )}
                        {/* Pending sale: paid-but-not-shipped eBay units
                            attributed to this group. Already moved out of
                            the unsold qty server-side; this chip surfaces
                            the count so the operator knows there's work to
                            do (ship + run update_sales). */}
                        {Number(row.unfulfilled_qty) > 0 && Number(row.oversold_qty) === 0 && (
                          <Tooltip title={`${row.unfulfilled_qty} unit(s) sold on eBay and awaiting shipment. Run update_sales after shipping to clear.`}>
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`${row.unfulfilled_qty} pending sale`}
                              sx={{ height: 22, fontWeight: 600 }}
                            />
                          </Tooltip>
                        )}
                        {(() => {
                          const imb = row.listing_imbalance;
                          const listed = Number(row.listed_qty) || 0;
                          const unsold = Number(row.unsold_qty) || 0;
                          const busy = adjustingKeys.has(row.key);
                          if (imb === "overlisted") return (
                            <Tooltip title={`eBay shows ${listed} listed but ${unsold} unsold on hand. Click to set qty to ${unsold}.`}>
                              <Chip
                                size="small" color="warning" variant="outlined"
                                label={busy ? "Adjusting…" : `over ${listed}/${unsold} → fix`}
                                onClick={(e) => { e.stopPropagation(); if (!busy) adjustQty(row); }}
                                disabled={busy}
                                clickable
                                sx={{ height: 22 }}
                              />
                            </Tooltip>
                          );
                          if (imb === "underlisted") return (
                            <Tooltip title={`eBay shows ${listed} listed but ${unsold} unsold on hand. Click to set qty to ${unsold}.`}>
                              <Chip
                                size="small" color="info" variant="outlined"
                                label={busy ? "Adjusting…" : `under ${listed}/${unsold} → fix`}
                                onClick={(e) => { e.stopPropagation(); if (!busy) adjustQty(row); }}
                                disabled={busy}
                                clickable
                                sx={{ height: 22 }}
                              />
                            </Tooltip>
                          );
                          if (imb === "unlisted" && unsold > 0) return (
                            <Tooltip title={`${unsold} unsold and not listed.`}>
                              <Chip size="small" color="default" variant="outlined" label="unlisted" sx={{ height: 22 }} />
                            </Tooltip>
                          );
                          return null;
                        })()}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {formatMoney(row.avg_cost)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {formatMoney(row.total_spend)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {formatMoney(row.total_value)}
                    </TableCell>
                    <TableCell>
                      {locs.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      ) : (
                        <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ maxWidth: 180 }}>
                          {locs.map((l) => (
                            <Chip key={l} size="small" label={l} variant="outlined" />
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      {conds.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      ) : (
                        <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ maxWidth: 180 }}>
                          {condsShown.map((c) => (
                            <Chip key={c} size="small" label={c} variant="outlined" />
                          ))}
                          {condsExtra > 0 && (
                            <Chip size="small" label={`+${condsExtra} more`} variant="outlined" />
                          )}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.linked_listings_count > 0 ? (
                        <Chip
                          size="small"
                          label={`${row.linked_listings_count} listed`}
                          color="primary"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admint/listings?q=${encodeURIComponent(row.cleaned_title || row.title || "")}`);
                          }}
                          sx={{ cursor: "pointer" }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {row.listed && (
                          <Chip size="small" label="Listed" color="primary" variant="outlined" />
                        )}
                        {sold > 0 && (
                          <Chip size="small" label={`${sold} sold`} color="success" variant="outlined" />
                        )}
                        {active > 0 && !row.listed && (
                          <Chip size="small" label={`${active} idle`} variant="outlined" />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!consolidate && rows.map((row, rowIndex) => {
                const spend = (Number(row.cost) || 0) + (Number(row.tax_cost) || 0) + (Number(row.premium_fee) || 0);
                const purchasedAt = String(row?.purchased_at || "").toLowerCase();
                const isWiz = purchasedAt.includes("wiz") && row?.item_id;
                const selected = selectedKeys.has(row.lot_id);
                // Click anywhere except controls → inventory detail by lot_id.
                // Consolidated rows already navigate to the title-slug key;
                // per-lot rows now use lot_id directly. The InventoryDetail
                // page accepts either since the backend resolves both.
                const handleRowClick = (e) => {
                  if (e.metaKey || e.ctrlKey || e.button === 1) return;
                  const t = e.target;
                  if (t.closest("button, a, input, textarea, select, .MuiChip-root, [role='button']")) return;
                  if (row.lot_id) {
                    navigate(`/admint/inventory/${encodeURIComponent(row.lot_id)}`);
                  }
                };
                return (
                  <TableRow
                    key={row.lot_id ?? row.item_id}
                    hover
                    selected={selected}
                    onClick={row.lot_id ? handleRowClick : undefined}
                    sx={row.lot_id ? { cursor: "pointer" } : undefined}
                    title={row.lot_id ? "Open inventory item" : undefined}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected}
                        onClick={(e) => { e.stopPropagation(); toggleAt(rowIndex, e.shiftKey); }}
                        onChange={() => { /* handled in onClick to read shiftKey */ }}
                        inputProps={{ "aria-label": `select ${row.lot_id}` }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 84 }}>
                      <ImageGalleryCell imageUrlsStr={row.image_urls} thumbnailSize={48} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'mono', fontSize: '0.78rem' }}>
                      <Box component="span" sx={isWiz ? { color: 'primary.main' } : undefined}>
                        {row.lot_id}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 0, maxWidth: 360 }}>
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.cleaned_title || row.title || "—"}
                        </Typography>
                        {row.brand && (
                          <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                            {row.brand}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell><StatusChip row={row} /></TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {formatMoney(row.value)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {formatMoney(spend)}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{formatDate(row.purchase_date)}</TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={row.condition || ""}
                        onChange={(e) => updateField(row.lot_id, "condition", e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        displayEmpty
                        sx={{ minWidth: 130 }}
                      >
                        {CONDITION_OPTIONS.map((o) => (
                          <MenuItem key={o} value={o}>{o || "—"}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Select
                          size="small"
                          value={
                            // Allow the rendered Select to show
                            // "Returned (Assurance)" even though it
                            // isn't in the dropdown options — picking
                            // it from the menu doesn't update cost, so
                            // operators must use the button below.
                            LOCATION_OPTIONS.includes(row.location || "")
                              ? (row.location || "")
                              : (row.location || "")
                          }
                          onChange={(e) => updateField(row.lot_id, "location", e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          displayEmpty
                          sx={{ minWidth: 130 }}
                        >
                          {LOCATION_OPTIONS.map((o) => (
                            <MenuItem key={o} value={o}>{o || "—"}</MenuItem>
                          ))}
                          {/* If the item is already in the Returned (Assurance) state,
                              render an extra option so the Select can show its current
                              value instead of falling back to blank. */}
                          {row.location === "Returned (Assurance)" && (
                            <MenuItem key="ra-current" value="Returned (Assurance)" disabled>
                              Returned (Assurance)
                            </MenuItem>
                          )}
                        </Select>
                        <Tooltip title="Item actions — mark refunded / returned / personal">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setItemMenuAnchor(e.currentTarget);
                              setItemMenuLot(row.lot_id);
                              setItemMenuCost(row.cost || 0);
                            }}
                            sx={{ p: 0.5 }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && rows.length > 0 && (
          <Box sx={{
            position: 'absolute', top: 8, right: 12,
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            bgcolor: 'background.paper', border: 1, borderColor: 'divider',
            borderRadius: 1, px: 1, py: 0.25, fontSize: '0.7rem',
            color: 'text.secondary',
          }}>
            <CircularProgress size={12} /> Refreshing
          </Box>
        )}
        <Box sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <TablePagination
            component="div"
            count={total}
            page={Math.max(0, page - 1)}
            onPageChange={(_, p) => setPage(p + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Box>
      </Paper>
      {/* Per-item action menu — opened by the kebab in each row's
          location cell. Single instance mounted at page level so we
          don't pay React for one menu per row. */}
      <Menu
        anchorEl={itemMenuAnchor}
        open={Boolean(itemMenuAnchor)}
        onClose={closeItemMenu}
      >
        <MenuItem
          onClick={() => { const lid = itemMenuLot; closeItemMenu(); if (lid) markRefunded(lid); }}
        >
          Mark as Refunded ($0 net, kept)
        </MenuItem>
        <MenuItem
          onClick={() => { const lid = itemMenuLot; const c = itemMenuCost; closeItemMenu(); if (lid) markReturnedAssurance(lid, c); }}
        >
          Mark as Returned (Assurance — ${(Number(itemMenuCost || 0) >= 100 ? 10 : 5).toFixed(2)} fee)
        </MenuItem>
        <MenuItem
          onClick={() => { const lid = itemMenuLot; closeItemMenu(); if (lid) markPersonal(lid); }}
        >
          Mark as Personal (kept for self)
        </MenuItem>
      </Menu>
    </Box>
  );
}
